import type { AgentRunRow, LaneTypeRow } from '../../lib/database.types'
import { ATTEMPT_CAP, stageFor, type Stage } from './stage'

/**
 * Runs grouped by the ticket they belong to.
 *
 * A lane is a slot and a run is one trip through it; neither says how a *ticket*
 * is going. DEV-11 took six runs across three lanes and opened two pull
 * requests, one of them abandoned, and on the lane board that is six unrelated
 * rows. This groups them so one line says what happened to the ticket.
 *
 * The two notions of "again" are counted separately and never added:
 *
 *   attempt   retries *inside* one run. `agent_runs.attempt` defaults to 1 and
 *             is incremented only by that run's own retries, so it resets with
 *             every new run row.
 *   runs      trips *around* the loop -- separate run rows for the same ticket.
 *
 * A ticket on its second build run, attempt 1, is two runs and attempt 1. It is
 * not three of anything, and reading it as three is a misreading that has
 * already happened once.
 *
 * This reads the rows `useRuns()` has already fetched -- no query, no request.
 * That makes it a window rather than a history: `useRuns()` takes the RUNS_WINDOW
 * most recent runs (per department where a view is about one), so a ticket whose runs have all aged out of that window is not
 * here at all. The view says so rather than implying completeness.
 */

/** One (department, lane_type) slot this ticket has been through, and how often. */
export interface TicketLane {
  key: string
  department: string
  laneType: string
  runs: number
}

export interface Ticket {
  /** `external_ref` -- 'DEV-11'. The grouping key, and never null here. */
  ref: string
  title: string | null
  url: string | null
  /** From the most recent run: a ticket can cross departments, and this follows it. */
  department: string
  /**
   * `owner/name` from the newest run that carries one, or null. The dispatcher
   * fills it from the ticket's Linear project through the repo routing, so it
   * is the nearest thing a run row has to the project the ticket is in.
   */
  repo: string | null
  laneType: string
  status: string
  stage: Stage
  /** Retries inside the latest run. Never added to `runs`. */
  attempt: number
  attemptCap: number
  /** Trips around the loop: how many run rows this ticket has. */
  runs: number
  /** Runs per lane, in the order the ticket went through them. */
  byLane: TicketLane[]
  /**
   * One line about the latest run when that run failed, and null otherwise.
   *
   * `agent_runs` carries no error text -- the only failure prose in the schema
   * is `approvals.context.failure.error`, which is a different table and a
   * different query. So this summarises what the run rows do say: which lane
   * stopped and which attempt it stopped on.
   */
  failureSummary: string | null
  /** Every distinct pr_url the ticket has produced, oldest first. */
  prUrls: string[]
  /** Sum of cost_usd, which is the only figure on a run that may be summed. */
  spend: number
  /** False when no run recorded a cost, so the view can say '—' and not '$0.00'. */
  hasCost: boolean
  /** created_at of the most recent run. */
  latestAt: string
}

const time = (iso: string): number => {
  const t = Date.parse(iso)
  return Number.isNaN(t) ? 0 : t
}

/**
 * The runs that are ticket work: every run but those of an unclaimed lane.
 *
 * An unclaimed lane (lane_types.claimed false -- today only data-dq) records
 * runs that nothing queued or claimed: one per nightly report a project sends,
 * with the project's ref where a ticket's would be. Grouped by that ref they
 * look like tickets and are not, so the desk and the lanes view's ticket list
 * leave them out. The lane itself still shows on the lanes board; its runs are
 * real, they are just not tickets.
 *
 * Until the registry is read -- or if it cannot be -- nothing is left out, which
 * is how the page read before the flag existed rather than a guess about which
 * lanes are which.
 */
export function ticketRuns(
  runs: ReadonlyArray<AgentRunRow>,
  lanes: ReadonlyArray<LaneTypeRow> = [],
): AgentRunRow[] {
  const unclaimed = new Set(lanes.filter((lane) => !lane.claimed).map((lane) => lane.lane_type))
  return runs.filter((run) => !unclaimed.has(run.lane_type))
}

export function toTickets(runs: ReadonlyArray<AgentRunRow>): Ticket[] {
  // A run with no external_ref belongs to no ticket. It is dropped rather than
  // collected under an 'Unknown' heading -- a placeholder bucket would read as
  // one badly-behaved ticket with twelve runs, which is the opposite of true.
  const byRef = new Map<string, AgentRunRow[]>()
  for (const run of runs) {
    const ref = run.external_ref
    if (!ref) continue
    const held = byRef.get(ref)
    if (held) held.push(run)
    else byRef.set(ref, [run])
  }

  return [...byRef.entries()]
    .map(([ref, rows]) => {
      // Oldest first: the per-lane order and the pull requests both read as the
      // path the ticket took, and the newest run is then simply the last one.
      const newestFirst = [...rows].sort((a, b) => time(b.created_at) - time(a.created_at))
      const ordered = [...newestFirst].reverse()
      // Unlike a lane slot, which lets a live run outrank a newer finished one
      // because a finished run must not hide work in flight, a ticket's latest
      // run is just its latest run. There is no slot here to compete for.
      const latest = newestFirst[0]!

      const lanes = new Map<string, TicketLane>()
      const prUrls: string[] = []
      let spend = 0
      let hasCost = false

      for (const run of ordered) {
        const key = `${run.department}/${run.lane_type}`
        const lane = lanes.get(key) ?? { key, department: run.department, laneType: run.lane_type, runs: 0 }
        lane.runs += 1
        lanes.set(key, lane)

        if (run.pr_url && !prUrls.includes(run.pr_url)) prUrls.push(run.pr_url)

        if (run.cost_usd != null) {
          const cost = Number(run.cost_usd)
          if (Number.isFinite(cost)) {
            spend += cost
            hasCost = true
          }
        }
        // input_tokens, output_tokens and the two cache columns are deliberately
        // not accumulated here. They are priced differently, so a per-ticket
        // total built from them would not be the basis of any charge; cost_usd
        // is the authoritative figure and it is the only one summed.
      }

      return {
        ref,
        // Title and url describe the ticket rather than the run, so the newest
        // run that carries one wins and a run that carries none does not blank
        // them -- a lane row with a null title should not cost the ticket its
        // name.
        title: newestFirst.find((r) => r.title)?.title ?? null,
        url: newestFirst.find((r) => r.external_url)?.external_url ?? null,
        department: latest.department,
        repo: newestFirst.find((r) => r.repo)?.repo ?? null,
        laneType: latest.lane_type,
        status: latest.status,
        stage: stageFor(latest.status, latest.attempt),
        attempt: latest.attempt,
        attemptCap: ATTEMPT_CAP,
        runs: ordered.length,
        byLane: [...lanes.values()],
        failureSummary:
          latest.status === 'failed'
            ? `${latest.lane_type} stopped on attempt ${latest.attempt} of ${ATTEMPT_CAP}`
            : null,
        prUrls,
        spend,
        hasCost,
        latestAt: latest.created_at,
      }
    })
    // Most recently active first, which is the order the runs themselves arrive
    // in. Failures are not lifted to the top: this is a record of how work is
    // going, not a list of problems (and a list of problems is exactly the
    // urgency design the product rules forbid).
    .sort((a, b) => time(b.latestAt) - time(a.latestAt) || a.ref.localeCompare(b.ref))
}
